import React from 'react';
import { Trash2, Plus, X } from 'lucide-react';
import InputGroup from './InputGroup';
import { useTheme } from '../../contexts/ThemeContext';

const CustomSectionEditor = ({ section, onUpdate, onDelete }) => {
    const { isDarkMode } = useTheme();

    const handleAddItem = () => {
        const newItems = [...section.items, { id: Date.now(), title: "", subtext: "", subtitle: "", description: "" }];
        onUpdate(section.id, { items: newItems });
    };

    const handleRemoveItem = (itemId) => {
        const newItems = section.items.filter(item => item.id !== itemId);
        onUpdate(section.id, { items: newItems });
    };

    const handleUpdateItem = (itemId, field, value) => {
        const newItems = section.items.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
        );
        onUpdate(section.id, { items: newItems });
    };

    const handleTitleChange = (e) => {
        onUpdate(section.id, { title: e.target.value });
    };

    return (
        <section className="mb-8 relative group/section">
            <div className="flex justify-between items-center mb-4">
                <input
                    type="text"
                    value={section.title}
                    onChange={handleTitleChange}
                    className={`text-lg font-bold bg-transparent border-b border-transparent focus:outline-none transition-colors w-full mr-4
                        ${isDarkMode
                            ? 'text-white hover:border-slate-600 focus:border-blue-500 placeholder-slate-500'
                            : 'text-slate-800 hover:border-slate-300 focus:border-blue-500 placeholder-slate-400'}`}
                    placeholder="Section Title"
                />
                <button
                    onClick={() => onDelete(section.id)}
                    className="text-slate-400 hover:text-red-500 p-1"
                    title="Delete Section"
                >
                    <Trash2 size={16} />
                </button>
            </div>

            <div className="space-y-4">
                {section.items.map((item) => (
                    <div key={item.id} className={`p-4 rounded-xl border relative group ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                        <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="absolute top-2 right-2 text-slate-300 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X size={16} />
                        </button>

                        <div className="grid grid-cols-1 gap-3 mb-3">
                            <InputGroup
                                label="Title / Role"
                                value={item.title}
                                onChange={(e) => handleUpdateItem(item.id, 'title', e.target.value)}
                                placeholder="e.g. Project Name or Role"
                            />
                            <InputGroup
                                label="Subtext / Company / Degree"
                                value={item.subtext}
                                onChange={(e) => handleUpdateItem(item.id, 'subtext', e.target.value)}
                                placeholder="e.g. Company Name or Degree"
                            />
                            <InputGroup
                                label="Subtitle / Date"
                                value={item.subtitle}
                                onChange={(e) => handleUpdateItem(item.id, 'subtitle', e.target.value)}
                                placeholder="e.g. 2023 - Present"
                            />
                        </div>
                        <InputGroup
                            multiline
                            label="Description"
                            value={item.description}
                            onChange={(e) => handleUpdateItem(item.id, 'description', e.target.value)}
                            placeholder="Description..."
                        />
                    </div>
                ))}

                <button
                    onClick={handleAddItem}
                    className={`w-full py-3 border-2 border-dashed rounded-xl transition-all flex items-center justify-center gap-2
                        ${isDarkMode
                            ? 'border-slate-700 text-slate-500 hover:border-blue-500 hover:text-blue-400 hover:bg-slate-800'
                            : 'border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50'}`}
                >
                    <Plus size={16} />
                    <span>Add Item</span>
                </button>
            </div>
        </section>
    );
};

export default CustomSectionEditor;
